import { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type {
  ReportQuery,
  ReportSearch,
  ReportSearchPage,
  ReportSearchStat,
} from '../types/report.types';
import { EmptyNote } from './MoreSections';
import {
  BODY_FG,
  CARD_BORDER,
  HEADING_FG,
  INNER_RULE,
  MUTED_FG,
  POSITIVE_FG,
  WORK_POST_BG,
  WORK_POST_FG,
  WORK_RULE_STRONG,
} from '../reportTokens';

/**
 * Website Search Performance's full design — design lines 390-478. Renders
 * only what `SearchSection` (ReportSections.tsx) shows once `search` is
 * already known to be `enabled`; that wrapper still owns the "Search Console
 * is not connected" empty state.
 *
 * `search.stats` is the one field here a v2 snapshot never sent (see
 * `ReportSearchStat` in report.types.ts) — the four-tile row simply does not
 * render without it, rather than a Pages/Queries pair with a blank strip
 * above them.
 */

/** Each stat's own display shape: how to print the headline number, and
 * (`ctr` only) a unit suffix on its delta to match the design's " pts".
 * Callers pass a `number` here, never the raw `stat.value` — `StatTile`
 * checks for null itself (see `ReportSearchStat.value`) and renders an em
 * dash without ever calling one of these, the same guard `PagesOnPage1` and
 * `QueriesRanking` already apply to a null `position`. */
const STAT_FORMAT: Record<string, (value: number) => string> = {
  clicks: value => value.toLocaleString(),
  impressions: value => value.toLocaleString(),
  ctr: value => `${value}%`,
  position: value => value.toFixed(1),
};
const DELTA_SUFFIX: Record<string, string> = { ctr: ' pts' };
const DELTA_DECIMALS: Record<string, number> = { ctr: 1, position: 1 };

/**
 * The backend pre-normalises every stat's delta so a positive number is
 * always the improvement — including `position`, where a FALLING number is
 * the good outcome (`ReportSearchStat.lowerIsBetter`; see
 * `services/hostedReport/searchStats.js`, which stores that one delta as
 * `previous - current` rather than the other three's `current - previous`).
 * Do not branch on `lowerIsBetter` here to flip the colour again — that
 * double-inverts exactly the stat the flag exists to protect, which is the
 * bug spotted in review that this comment (and the one on the type) is here
 * to prevent a repeat of.
 */
function StatDelta({
  delta,
  suffix = '',
  decimals = 0,
}: {
  delta: number | null;
  suffix?: string;
  decimals?: number;
}) {
  // Gains only: the client report shows improvements, so a loss or a flat
  // week leaves the figure on its own.
  if (delta === null || delta < (decimals > 0 ? 0.05 : 0.5)) return null;
  return (
    <Box
      component='span'
      sx={{ fontSize: '12px', fontWeight: 700, color: POSITIVE_FG }}
    >
      {`↑ ${delta.toFixed(decimals)}${suffix}`}
    </Box>
  );
}

function StatTile({ stat }: { stat: ReportSearchStat }) {
  const format = STAT_FORMAT[stat.key] ?? String;
  // Only `position` can actually be null (see `ReportSearchStat.value`), but
  // this is a single check for all four keys rather than trusting each
  // formatter to guard itself — the same "check before you call toFixed"
  // rule `PagesOnPage1`/`QueriesRanking` already follow for a row's position.
  const displayValue = stat.value === null ? '—' : format(stat.value);
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: INNER_RULE,
        borderRadius: '12px',
        padding: '14px 16px',
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: '12px', color: MUTED_FG, mb: '6px' }}>
        {stat.label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          component='span'
          sx={{
            fontSize: '26px',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          {displayValue}
        </Typography>
        <StatDelta
          delta={stat.delta}
          suffix={DELTA_SUFFIX[stat.key]}
          decimals={DELTA_DECIMALS[stat.key] ?? 0}
        />
      </Box>
    </Box>
  );
}

/** The four headline tiles — design lines 394-404. Absent entirely (not four
 * empty tiles) when `search.stats` is undefined, the v2 case. */
function StatTiles({ stats }: { stats?: ReportSearchStat[] }) {
  if (!stats || stats.length === 0) return null;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: '12px',
      }}
    >
      {stats.map(stat => (
        <StatTile key={stat.key} stat={stat} />
      ))}
    </Box>
  );
}

/** Rows past this many are behind the "Show all" toggle, both here and in
 * `QueriesRanking` — the count the brief calls for, not the design mockup's
 * own placeholder default of 8. */
const ROW_PAGE_SIZE = 5;

/**
 * The host most of the table's pages are on. Every row shares it, so it is
 * left off the label; a row on any other host keeps its host in front.
 */
function primaryHost(pages: ReportSearchPage[]): string {
  const counts = new Map<string, number>();
  for (const page of pages) {
    const host = hostOf(page.page);
    if (host) counts.set(host, (counts.get(host) || 0) + 1);
  }
  let best = '';
  let bestCount = 0;
  counts.forEach((count, host) => {
    if (count > bestCount) {
      best = host;
      bestCount = count;
    }
  });
  return best;
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
}

/**
 * What a page row reads as: its path, without the domain every row repeats.
 *
 * The full URL truncated after the domain, so every row read
 * "https://www.example.com/…" and the part that told pages apart was cut
 * off. The root reads as "Home Page" rather than a bare slash. A site
 * indexed on two hosts (www and bare) lists a homepage on each, so a row
 * off the main host keeps its host in front — otherwise two rows would both
 * read "Home Page". When the two hosts differ only by "www." — the usual
 * case — that is all the row says, since a whole domain in front of the
 * path would truncate as badly as the URL did. Anything that will not parse
 * as a URL is shown as given.
 *
 * A row the report already merged across hosts (`addresses`) is one page on
 * all of them, so it never carries a host: its link is just its busiest
 * address, which may well not be on the main host.
 */
function pageLabel(page: ReportSearchPage, mainHost: string): string {
  const url = page.page;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  let path = `${parsed.pathname}${parsed.search}`;
  try {
    path = decodeURI(path);
  } catch {
    // A malformed escape: show it encoded rather than not at all.
  }
  const label = path === '/' || path === '' ? 'Home Page' : path;
  if (parsed.host === mainHost) return label;
  if ((page.addresses?.length ?? 0) > 1) return label;
  const bare = (host: string) => host.replace(/^www\./, '');
  if (bare(parsed.host) === bare(mainHost)) {
    return `${label} (${parsed.host.startsWith('www.') ? 'www' : 'no www'})`;
  }
  return `${parsed.host} · ${label}`;
}

/** Both panels are the same card in the design, down to the pixel. */
const panelSx = {
  border: '1px solid',
  borderColor: INNER_RULE,
  borderRadius: '14px',
  padding: { xs: '16px', sm: '20px' },
  minWidth: 0,
} as const;

const panelTitleSx = {
  fontSize: '17px',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  mb: '4px',
} as const;

const panelSubtitleSx = {
  fontSize: '13px',
  color: MUTED_FG,
  mb: '14px',
} as const;

/** The design's narrower table: 13px body, tight rows, every column but the
 * first right-aligned, and no inside gutters on the outer two. */
const searchTableSx = {
  width: '100%',
  borderCollapse: 'collapse',
  '& th': {
    textAlign: 'right',
    padding: '8px',
    color: BODY_FG,
    fontWeight: 600,
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${CARD_BORDER}`,
    whiteSpace: 'nowrap',
  },
  '& th:first-of-type': { textAlign: 'left', padding: '8px 0' },
  '& th:last-of-type': { padding: '8px 0 8px 8px' },
  '& td': {
    textAlign: 'right',
    padding: '11px 8px',
    borderBottom: `1px solid ${INNER_RULE}`,
    fontSize: '13px',
  },
  '& td:first-of-type': { textAlign: 'left', padding: '11px 0' },
  '& td:last-of-type': { padding: '11px 0 11px 8px' },
  // On a phone the card is about 290px wide. At desktop spacing the number
  // columns pushed Clicks and Position off the edge, behind a sideways scroll
  // nothing pointed to.
  '@media (max-width: 599.95px)': {
    '& th': { padding: '8px 5px' },
    '& th:first-of-type': { padding: '8px 6px 8px 0' },
    '& th:last-of-type': { padding: '8px 0 8px 5px' },
    '& td': { padding: '10px 5px' },
    '& td:first-of-type': { padding: '10px 6px 10px 0' },
    '& td:last-of-type': { padding: '10px 0 10px 5px' },
  },
} as const;

/** The widest table the desktop layout wants; a phone gets the card's width. */
const tableMinWidth = { xs: 0, sm: 380 } as const;

/** "Position" is the widest header, and on a phone the column it heads is
 * worth more than the word: it reads "Pos." there. */
function PositionHeader() {
  return (
    <>
      <Box component='span' sx={{ display: { xs: 'none', sm: 'inline' } }}>
        Position
      </Box>
      <Box
        component='span'
        aria-label='Position'
        sx={{ display: { xs: 'inline', sm: 'none' } }}
      >
        Pos.
      </Box>
    </>
  );
}

/**
 * The headline count above each table: "4", "of 5 indexed pages", and how
 * far that rose.
 *
 * Only a rise is drawn. The client report shows improvements only, so a fall,
 * no change, and a null `change` (no earlier week to compare against) all
 * leave the count on its own.
 */
function CountBox({
  count,
  of,
  change,
}: {
  count: number;
  of: string;
  change?: number | null;
}) {
  const rose = change !== null && change !== undefined && change > 0;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '10px',
        flexWrap: 'wrap',
        border: '1px solid',
        borderColor: INNER_RULE,
        borderRadius: '12px',
        padding: '14px 16px',
        mb: '16px',
      }}
    >
      <Typography
        component='span'
        sx={{
          fontSize: '32px',
          fontWeight: 800,
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}
      >
        {count}
      </Typography>
      <Typography component='span' sx={{ fontSize: '13px', color: MUTED_FG }}>
        {of}
      </Typography>
      {rose && (
        <Typography
          component='span'
          sx={{
            ml: 'auto',
            fontSize: '12px',
            fontWeight: 700,
            color: POSITIVE_FG,
          }}
        >
          {`↑ ${change} from last week`}
        </Typography>
      )}
    </Box>
  );
}

/** "▾ Show all 9" / "▴ Show less", shared by both panels. */
function ShowAllToggle({
  total,
  expanded,
  onToggle,
}: {
  total: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (total <= ROW_PAGE_SIZE) return null;
  return (
    <Box
      component='button'
      type='button'
      className='report-no-print'
      onClick={onToggle}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        mt: '14px',
        border: 0,
        bgcolor: 'transparent',
        p: 0,
        fontSize: '13px',
        fontWeight: 600,
        color: WORK_RULE_STRONG,
        cursor: 'pointer',
      }}
    >
      {expanded ? '▴ Show less' : `▾ Show all ${total}`}
    </Box>
  );
}

/** True when a position is on Google's first page — the same "page 1"
 * definition `calculatePerformanceMetricsFromGSC` uses server-side
 * (position 1-10), applied here per row to shade that row in rather than
 * inventing a second, table-only definition. */
const onPageOne = (position: number | null) =>
  position !== null && position <= 10;

/** URL, Impr., Clicks, Position — design lines 414-440. A page's whole row
 * dims when it fell off page 1, and only the Position cell recolours to
 * green, mirroring the design's own `fg`/`posFg` split (lines 851-852) rather
 * than tinting every cell the same way. */
function PagesOnPage1({ search }: { search: ReportSearch }) {
  const [expanded, setExpanded] = useState(false);
  const rows: ReportSearchPage[] = search.pages;
  const visible = expanded ? rows : rows.slice(0, ROW_PAGE_SIZE);
  const mainHost = primaryHost(rows);

  return (
    <Box sx={panelSx}>
      <Typography sx={panelTitleSx}>Pages on Page 1</Typography>
      <Typography sx={panelSubtitleSx}>
        From Google Search Console, last 7 days.
      </Typography>
      <CountBox
        count={search.pagesOnPage1}
        of={`of ${search.pagesTracked} indexed page${search.pagesTracked === 1 ? '' : 's'}`}
        change={search.pagesOnPage1Change}
      />
      {rows.length === 0 ? (
        <EmptyNote>
          No pages recorded Search Console activity this week.
        </EmptyNote>
      ) : (
        <>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ ...searchTableSx, minWidth: tableMinWidth }}>
              <TableHead>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell align='right'>Impr.</TableCell>
                  <TableCell align='right'>Clicks</TableCell>
                  <TableCell align='right'>
                    <PositionHeader />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map(page => {
                  const ranked = onPageOne(page.position);
                  return (
                    <TableRow key={page.page}>
                      <TableCell sx={{ maxWidth: { xs: 'none', sm: 260 } }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Box
                            component='a'
                            href={page.page}
                            title={
                              page.addresses && page.addresses.length > 1
                                ? `Combined: ${page.addresses.join(' and ')}`
                                : page.page
                            }
                            target='_blank'
                            rel='noopener'
                            sx={{
                              fontSize: '13px',
                              color: ranked ? HEADING_FG : MUTED_FG,
                              textDecoration: 'none',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              // A phone has no hover to show the full URL, so
                              // the path wraps there rather than being cut.
                              whiteSpace: { xs: 'normal', sm: 'nowrap' },
                              overflowWrap: 'anywhere',
                              maxWidth: { xs: 'none', sm: 220 },
                              '&:hover': {
                                color: WORK_POST_FG,
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {pageLabel(page, mainHost)}
                          </Box>
                          {page.isNew && (
                            <Box
                              component='span'
                              sx={{
                                fontSize: '10px',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: WORK_POST_FG,
                                bgcolor: WORK_POST_BG,
                                padding: '2px 7px',
                                borderRadius: '9999px',
                              }}
                            >
                              New
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ color: ranked ? HEADING_FG : MUTED_FG }}
                      >
                        {page.impressions.toLocaleString()}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ color: ranked ? HEADING_FG : MUTED_FG }}
                      >
                        {page.clicks.toLocaleString()}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{
                          fontWeight: 700,
                          color: ranked ? POSITIVE_FG : MUTED_FG,
                        }}
                      >
                        {page.position === null
                          ? '—'
                          : page.position.toFixed(1)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ShowAllToggle
            total={rows.length}
            expanded={expanded}
            onToggle={() => setExpanded(value => !value)}
          />
        </>
      )}
    </Box>
  );
}

/**
 * Query, Impr., Clicks, Position — design lines 446-477, the same table shape
 * as `PagesOnPage1` minus the link and "New" badge (`ReportQuery` carries
 * neither a href nor an `isNew` flag).
 *
 * The count line reads "queries ranking this week" rather than the design's
 * "of N ranking queries" — `buildSearch` (hostedReportSnapshot.js) caps
 * `queries` at its 10 biggest movers, the same way it caps `pages`, but
 * unlike pages there is no separate `queriesTracked` total to be "of". Stating
 * one here would be inventing a denominator the backend never sent.
 */
function QueriesRanking({ search }: { search: ReportSearch }) {
  const [expanded, setExpanded] = useState(false);
  const rows: ReportQuery[] = search.queries;
  const visible = expanded ? rows : rows.slice(0, ROW_PAGE_SIZE);

  // The design's "3 of 5 ranking queries on page 1". Both figures come from
  // `buildSearchCoverage`, which counts DISTINCT queries across the whole
  // period — `rows` is only the ten biggest movers the snapshot stores, so
  // counting it would quietly cap the denominator at ten.
  const onPage1 = search.queriesOnPage1;
  const tracked = search.queriesTracked;

  return (
    <Box sx={panelSx}>
      <Typography sx={panelTitleSx}>Queries Ranking</Typography>
      <Typography sx={panelSubtitleSx}>
        Search queries bringing impressions to the site this week.
      </Typography>
      {/* A snapshot built before the counts existed, or without GSC rows in
          hand, falls back to counting the stored rows and says only that —
          rather than printing "of null" or an invented denominator. */}
      {onPage1 != null && tracked != null ? (
        <CountBox
          count={onPage1}
          of={`of ${tracked} ranking quer${tracked === 1 ? 'y' : 'ies'} on page 1`}
          change={search.queriesOnPage1Change}
        />
      ) : (
        <CountBox
          count={rows.length}
          of={`quer${rows.length === 1 ? 'y' : 'ies'} ranking this week`}
        />
      )}
      {rows.length === 0 ? (
        <EmptyNote>
          No queries brought impressions to the site this week.
        </EmptyNote>
      ) : (
        <>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ ...searchTableSx, minWidth: tableMinWidth }}>
              <TableHead>
                <TableRow>
                  <TableCell>Query</TableCell>
                  <TableCell align='right'>Impr.</TableCell>
                  <TableCell align='right'>Clicks</TableCell>
                  <TableCell align='right'>
                    <PositionHeader />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((row, index) => {
                  const ranked = onPageOne(row.position);
                  return (
                    <TableRow key={`${row.query}-${index}`}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: ranked ? HEADING_FG : MUTED_FG,
                          maxWidth: { xs: 'none', sm: 260 },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {row.query}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ color: ranked ? HEADING_FG : MUTED_FG }}
                      >
                        {(row.impressions ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ color: ranked ? HEADING_FG : MUTED_FG }}
                      >
                        {row.clicks ?? 0}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{
                          fontWeight: 700,
                          color: ranked ? POSITIVE_FG : MUTED_FG,
                        }}
                      >
                        {row.position === null ? '—' : row.position.toFixed(1)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ShowAllToggle
            total={rows.length}
            expanded={expanded}
            onToggle={() => setExpanded(value => !value)}
          />
        </>
      )}
    </Box>
  );
}

/**
 * The whole Website Search Performance body: the four stat tiles, Pages on
 * Page 1 and Queries Ranking — everything `SearchSection`
 * (ReportSections.tsx) renders once `search.enabled` is true.
 */
export function SearchPerformanceSection({ search }: { search: ReportSearch }) {
  return (
    <Box>
      <StatTiles stats={search.stats} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(auto-fit, minmax(340px, 1fr))',
          },
          gap: '20px',
          alignItems: 'start',
          mt: '20px',
        }}
      >
        <PagesOnPage1 search={search} />
        <QueriesRanking search={search} />
      </Box>
    </Box>
  );
}
